import React, { useEffect, useState } from "react";
import { api } from "../utils/api";
import { useTheme } from "../context/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { AlertTriangle, Beer, Users, TrendingUp, DollarSign, Calendar, Gift, Wifi, MessageCircle, Package, Check, TrendingDown, Wallet, Filter } from "lucide-react";
import { toast } from "sonner";

const MetricCard = ({ title, value, icon: Icon, color = "primary" }) => {
  const borderClass = "hover:border-" + color;
  const bgIconClass = "bg-" + color + "/10";
  const textIconClass = "text-" + color;

  return (
    <Card className={"bg-card border border-border transition-all h-full " + borderClass}>
      <CardContent className="p-4 flex items-center justify-between h-full">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">{title}</p>
          <p className="text-2xl font-bold font-mono">{value}</p>
        </div>
        <div className={"p-2 rounded-full " + bgIconClass}>
          <Icon className={"h-6 w-6 " + textIconClass} />
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { pixQRCodeUrl } = useTheme();

  // Estado para controlar o mês/ano do filtro (Padrão: Mês Atual)
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Toda vez que o selectedMonth mudar, ele busca os dados novamente
  useEffect(() => {
    fetchStats();
  }, [selectedMonth]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      let params = {};
      if (selectedMonth) {
        // Converte "2026-02" para o primeiro e último dia do mês
        const [year, month] = selectedMonth.split('-');
        const start_date = `${year}-${month}-01`;
        const end_date = new Date(year, month, 0).toISOString().split('T')[0];
        params = { start_date, end_date };
      }
      
      const response = await api.reports.getDashboard(params);
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = (customer) => {
    let phoneNum = customer.phone ? customer.phone.replace(/\D/g, '') : '';
    
    if (!phoneNum) {
      toast.error("Cliente sem telefone cadastrado.");
      return;
    }
    
    if (phoneNum.length <= 11) {
      phoneNum = '55' + phoneNum;
    }

    const message = `Fala ${customer.name}, tudo bem com a máquina? Cara, consta um saldo pendente de R$ ${customer.balance.toFixed(2)} no Cabrones com mais de 30 dias. Quando puder dar uma conferida, agradeço!`;
    const url = `https://wa.me/${phoneNum}?text=${encodeURIComponent(message)}`;
    
    window.open(url, '_blank');
  };

  // Cálculos do Raio-X Financeiro
  const grossRevenue = stats?.total_revenue || 0;
  const totalCosts = stats?.total_costs || 0;
  const netRevenue = stats?.net_revenue || 0;
  
  const costPercent = grossRevenue > 0 ? ((totalCosts / grossRevenue) * 100).toFixed(1) : 0;
  const netPercent = grossRevenue > 0 ? ((netRevenue / grossRevenue) * 100).toFixed(1) : 0;

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-muted-foreground font-mono">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4 w-full pb-4" data-testid="dashboard-page">
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold uppercase tracking-tight">Dashboard</h1>
        
        {/* BARRA DE FILTRO DE MÊS */}
        <div className="flex items-center gap-2 bg-card border border-border px-3 py-1.5 rounded-md shadow-sm">
          <Filter className="h-4 w-4 text-primary" />
          <span className="text-xs uppercase font-bold text-muted-foreground hidden sm:inline">Período:</span>
          <input 
            type="month" 
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="bg-transparent border-none text-sm font-mono focus:ring-0 cursor-pointer text-foreground outline-none"
          />
        </div>
      </div>

      {/* LINHA 1: MÉTRICAS SUPERIORES (Agora afetadas pelo filtro do mês) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title="Produtos" value={stats?.total_products || 0} icon={Beer} color="primary" />
        <MetricCard title="Clientes" value={stats?.total_customers || 0} icon={Users} color="primary" />
        <MetricCard title="Faturamento Bruto" value={"R$ " + (stats?.total_revenue || 0).toFixed(2)} icon={DollarSign} color="primary" />
        <MetricCard title="Contas Abertas" value={"R$ " + (stats?.total_debt || 0).toFixed(2)} icon={TrendingUp} color="destructive" />
      </div>

      {/* LINHA 2: RAIO-X FINANCEIRO */}
      <Card className="bg-card border-border w-full">
        <CardHeader className="p-4 pb-2 border-b flex flex-row items-center justify-between">
          <CardTitle className="text-sm uppercase tracking-wide flex items-center gap-2">
            <Wallet className="h-4 w-4 text-primary" />
            Raio-X Financeiro (Lucro Real)
          </CardTitle>
          <span className="text-xs font-mono text-muted-foreground">Competência: {selectedMonth.split('-').reverse().join('/')}</span>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-8 items-center justify-between">
            
            {/* Bloco de Valores */}
            <div className="w-full md:w-1/3 flex flex-col gap-4">
              <div className="flex justify-between items-center border-b border-border/50 pb-2">
                <span className="text-sm text-muted-foreground uppercase font-medium">Receita Bruta</span>
                <span className="font-mono font-bold text-lg">R$ {grossRevenue.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-border/50 pb-2">
                <span className="text-sm text-destructive uppercase font-medium flex items-center gap-1"><TrendingDown className="h-3 w-3"/> Custo (Mercadorias)</span>
                <span className="font-mono font-bold text-destructive">R$ {totalCosts.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-emerald-500 uppercase font-bold flex items-center gap-1"><TrendingUp className="h-3 w-3"/> Lucro Líquido</span>
                <span className="font-mono font-black text-emerald-500 text-xl">R$ {netRevenue.toFixed(2)}</span>
              </div>
            </div>

            {/* Bloco Gráfico (Barras Nativas) */}
            <div className="w-full md:w-2/3 flex flex-col gap-3">
              <div className="w-full">
                <div className="flex justify-between text-xs mb-1 font-mono uppercase text-muted-foreground">
                  <span>Custo ({costPercent}%)</span>
                  <span>Lucro ({netPercent}%)</span>
                </div>
                {/* A Barra de Progresso Combinada */}
                <div className="h-6 w-full bg-secondary rounded-full overflow-hidden flex shadow-inner">
                  {grossRevenue > 0 ? (
                    <>
                      <div 
                        className="h-full bg-destructive flex items-center justify-center text-[10px] font-bold text-white transition-all duration-500"
                        style={{ width: `${costPercent}%` }}
                      >
                        {costPercent > 10 ? `${costPercent}%` : ''}
                      </div>
                      <div 
                        className="h-full bg-emerald-500 flex items-center justify-center text-[10px] font-bold text-white transition-all duration-500"
                        style={{ width: `${netPercent}%` }}
                      >
                        {netPercent > 10 ? `${netPercent}%` : ''}
                      </div>
                    </>
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground font-mono">Sem dados de vendas neste mês</div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* LINHA 3: CORPO PRINCIPAL (PIX e ANIVERSARIANTES) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        
        {/* COLUNA ESQUERDA: APENAS PIX */}
        <Card className="bg-card border-border flex flex-col h-[450px]">
          <CardHeader className="p-3 pb-0 shrink-0">
            <CardTitle className="text-sm uppercase tracking-wide flex items-center gap-2">
              <span className="text-primary font-bold">Pix Bar</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 p-4 flex items-center justify-center overflow-hidden">
              {pixQRCodeUrl ? (
                <div className="bg-white p-2 rounded h-full aspect-square flex items-center justify-center mx-auto shadow-sm">
                  <img src={pixQRCodeUrl} alt="Pix" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">Sem QR Code configurado</p>
              )}
          </CardContent>
        </Card>

        {/* COLUNA DIREITA: ANIVERSARIANTES + INFO CLUBE */}
        <div className="flex flex-col space-y-4 h-[450px]">
          <Card className="bg-card border-border flex flex-col flex-1 overflow-hidden">
            <CardHeader className="p-3 pb-0 shrink-0">
                <CardTitle className="text-sm uppercase tracking-wide flex items-center gap-2">
                  <Gift className="h-4 w-4 text-primary" />
                  Aniversariantes do Mes
                </CardTitle>
            </CardHeader>
            <CardContent className="p-3 flex-1 overflow-y-auto">
              {stats?.birthdays && stats.birthdays.length > 0 ? (
                <div className="space-y-2">
                  {stats.birthdays.map(b => {
                    const d = new Date(b.birth_date + "T12:00:00");
                    return (
                      <div key={b.id} className="flex justify-between items-center p-2 bg-secondary/30 rounded-md border border-border/50">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xs">
                            {b.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-foreground text-sm truncate max-w-[150px]">{b.name}</span>
                        </div>
                        <div className="flex items-center gap-1 text-primary font-mono bg-primary/10 px-2 py-0.5 rounded text-xs">
                            <Calendar className="h-3 w-3" />
                            <span className="font-bold">{d.getDate()}/{d.getMonth() + 1}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center h-full flex items-center justify-center text-muted-foreground">
                  <p className="text-xs">Nenhum aniversariante.</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border h-[75px] flex items-center shrink-0">
            <CardContent className="p-4 w-full">
              <div className="flex flex-row justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-[10px]">CNPJ</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">CNPJ do Clube</p>
                    <p className="font-mono font-bold text-sm">39.452.738/0001-25</p>
                  </div>
                </div>
                <div className="h-10 w-px bg-border"></div>
                <div className="flex items-center gap-3 text-right">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Rede WiFi</p>
                    <p className="font-mono font-bold text-sm">3133cmc3133</p>
                  </div>
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <Wifi className="h-5 w-5" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* LINHA 4: BLOCOS DE ALERTA SIMÉTRICOS (ESTOQUE E INADIMPLENTES) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
        
        {/* BLOCO ESQUERDA: ESTOQUE BAIXO */}
        <Card className={`border-2 flex flex-col h-[350px] ${stats?.low_stock_count > 0 ? 'bg-destructive/5 border-destructive' : 'bg-card border-border'}`}>
          <CardHeader className="p-4 pb-2 border-b shrink-0 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className={`h-5 w-5 ${stats?.low_stock_count > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
              <CardTitle className={`text-sm uppercase tracking-wide font-bold ${stats?.low_stock_count > 0 ? 'text-destructive' : ''}`}>Estoque Baixo</CardTitle>
            </div>
            <p className={`text-xs font-mono ${stats?.low_stock_count > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {stats?.low_stock_count || 0} item(s)
            </p>
          </CardHeader>
          <CardContent className="p-4 flex-1 overflow-y-auto">
            {stats?.low_stock_count > 0 ? (
              <div className="flex flex-wrap gap-2">
                {stats.low_stock_products.map((product) => (
                  <div key={product.id} className="px-3 py-2 bg-card border border-destructive/50 rounded flex-shrink-0 shadow-sm">
                    <p className="font-bold text-foreground text-sm mb-1">{product.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">Qtd: <span className="text-destructive font-bold">{product.stock}</span></p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <p className="text-sm flex items-center gap-2"><Check className="h-4 w-4" /> Estoque OK</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* BLOCO DIREITA: INADIMPLENTES */}
        <Card className={`border-2 flex flex-col h-[350px] ${stats?.overdue_customers?.length > 0 ? 'bg-destructive/5 border-destructive' : 'bg-card border-border'}`}>
          <CardHeader className="p-4 pb-2 border-b shrink-0 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${stats?.overdue_customers?.length > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
              <CardTitle className={`text-sm uppercase tracking-wide font-bold ${stats?.overdue_customers?.length > 0 ? 'text-destructive' : ''}`}>Inadimplentes (+30 dias)</CardTitle>
            </div>
            <p className={`text-xs font-mono ${stats?.overdue_customers?.length > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {stats?.overdue_customers?.length || 0} cliente(s)
            </p>
          </CardHeader>
          <CardContent className="p-4 flex-1 overflow-y-auto">
            {stats?.overdue_customers && stats.overdue_customers.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {stats.overdue_customers.map(c => (
                  <div key={c.customer_id} className="flex justify-between items-center p-3 bg-card rounded-md border border-destructive/30 shadow-sm">
                    <div>
                      <p className="font-bold text-foreground text-sm uppercase truncate max-w-[120px]">{c.name}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">Atraso: {c.oldest_debt_days} dias</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <p className="font-bold text-destructive font-mono text-sm">R$ {c.balance.toFixed(2)}</p>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1 font-bold uppercase text-[10px] h-7 px-2"
                        onClick={() => handleWhatsAppClick(c)}
                      >
                        <MessageCircle className="h-3.5 w-3.5" /> Cobrar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <p className="text-sm flex items-center gap-2"><Check className="h-4 w-4" /> Sem inadimplentes</p>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default Dashboard;
